import { useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Header, Footer } from 'components';

import SpekMesin from 'assets/images/spesifikasi-mesin-agate.png';
import BongkarTbs from 'assets/images/bongkar-tbs.png';
import TbsDitolak from 'assets/images/tbs-ditolak.png';
import GradingConfusionMatrixTabel from 'assets/images/grading-confusion-matrix-tabel.png';
import GradingConfusionMatrixStatus from 'assets/images/grading-confusion-matrix-status.png';
import GradingConfusionMatrixTabelEn from 'assets/images/grading-confusion-matrix-tabel-en.png';
import GradingConfusionMatrixStatusEn from 'assets/images/grading-confusion-matrix-status-en.png';
import TitleSection from 'components/agate/TitleSection';
import PostMeta from 'components/agate/PostMeta';
import ContentSection from 'components/agate/ContentSection';
import SpecTable from 'components/agate/SpecTable';
import ImageWithCaption from 'components/agate/ImageWithCaption';
import EvaluationTable from 'components/agate/EvaluationTable';
import EvaluationMobile from 'components/agate/EvaluationMobile';

import {
  paragraphsMainId,
  paragraphsMainEng,
  mesinSpecsId,
  mesinSpecsEng,
  agateProcessParagraphs1Id,
  agateProcessParagraphs1Eng,
  agateProcessParagraphs2Id,
  agateProcessParagraphs2Eng,
  agateProcessParagraphs3Id,
  agateProcessParagraphs3Eng,
  imageDataId,
  imageDataEng,
  accuracyParagraphs1Id,
  accuracyParagraphs1Eng,
  accuracyParagraphs2Id,
  accuracyParagraphs2Eng,
} from '../utils/data-agate';

export default function Agate() {
  const { Lang } = useParams();
  const history = useHistory();

  const isEnglish = useMemo(() => Lang === 'en', [Lang]);

  const goToPage = () => {
    Lang === 'en'
      ? history.push('/agate/kda/id')
      : history.push('/agate/kda/en');
  };

  return (
    <>
      <main>
        <Header />
        <section className='px-6 md:px-24 py-2'>
          <TitleSection
            title={
              isEnglish
                ? 'KDA successfully implement 100% external FFB sorting'
                : 'KDA berhasil menerapkan 100% sortasi TBS luar'
            }
            description={
              isEnglish
                ? 'KDA became the first palm oil mill to implement 100% automated sorting of external FFB using the AGATE machine. Within four months of its implementation in early 2025, the mill recorded up to 2.5% increase in oil extraction efficiency, with an estimated investment break-even point (BEP) for the AGATE system between 4 to 8 months.'
                : 'KDA menjadi pabrik pertama yang mengimplementasikan sortir otomatis 100% TBS luar menggunakan mesin AGATE. Empat bulan setelah implementasi awal 2025, efisiensi ekstraksi minyak naik 2,5% dengan estimasi Break Even Point (BEP) investasi mesin AGATE sekitar 4-8 bulan.'
            }
          />
          <PostMeta isEnglish={isEnglish} toggleLanguage={goToPage} />
          <article className='text-neutral-7 text-content leading-normal space-y-8'>
            <ContentSection
              paragraphs={isEnglish ? paragraphsMainEng : paragraphsMainId}
            />
          </article>
        </section>

        <section className='px-6 md:px-24 py-2 mt-20'>
          <h2 className='text-3xl md:text-4xl font-bold mb-4'>
            {isEnglish
              ? 'AGATE Machine Specifications'
              : 'Spesifikasi Mesin AGATE'}
          </h2>
          <div className='grid grid-cols-1 md:grid-cols-5 gap-10'>
            <div className='md:col-span-3'>
              <img
                src={SpekMesin}
                alt={
                  isEnglish
                    ? 'AGATE Machine Specifications'
                    : 'Spesifikasi Mesin AGATE'
                }
                className='w-full h-full'
              />
            </div>
            <SpecTable specs={isEnglish ? mesinSpecsEng : mesinSpecsId} />
          </div>
        </section>

        <section className='px-6 md:px-24 py-2 mt-10'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-10'>
            <div className='order-1 md:order-1'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4'>
                {isEnglish ? 'AGATE Machine Process' : 'Proses Mesin AGATE'}
              </h2>
              <article className='text-neutral-7 text-content leading-normal space-y-8 m-auto'>
                <ContentSection
                  paragraphs={
                    isEnglish
                      ? agateProcessParagraphs1Eng
                      : agateProcessParagraphs1Id
                  }
                />
              </article>
            </div>

            <div className='order-2 md:order-2'>
              <ImageWithCaption
                src={BongkarTbs}
                alt={isEnglish ? 'Process unloading FFB' : 'Proses bongkar TBS'}
                caption={
                  isEnglish ? 'Process unloading FFB' : 'Proses bongkar TBS'
                }
              />
            </div>

            <div className='order-4 md:order-3'>
              <ImageWithCaption
                src={TbsDitolak}
                alt={
                  isEnglish ? 'FFB reject from AGATE' : 'TBS ditolak dari AGATE'
                }
                caption={
                  isEnglish ? 'FFB reject from AGATE' : 'TBS ditolak dari AGATE'
                }
              />
            </div>

            <div className='order-3 md:order-4 text-neutral-7 text-content leading-normal space-y-8 m-auto'>
              <ContentSection
                paragraphs={
                  isEnglish
                    ? agateProcessParagraphs2Eng
                    : agateProcessParagraphs2Id
                }
              />
            </div>

            <div className='order-5 md:order-5 md:col-span-2 text-neutral-7 text-content leading-normal space-y-8'>
              <ContentSection
                paragraphs={
                  isEnglish
                    ? agateProcessParagraphs3Eng
                    : agateProcessParagraphs3Id
                }
              />
            </div>
          </div>
        </section>

        <section className='px-6 md:px-24 py-2 space-y-8 my-10 md:m-20'>
          {(isEnglish ? imageDataEng : imageDataId).map((image, index) => (
            <ImageWithCaption key={index} {...image} shadow='shadow-xl' />
          ))}
        </section>

        <section className='px-6 md:px-24 py-2'>
          <h2 className='md:text-4xl font-bold mb-4 text-center'>
            {isEnglish ? 'AGATE AI Accuracy' : 'Akurasi AI AGATE'}
          </h2>

          <article className='text-neutral-7 text-content leading-normal space-y-8 my-10'>
            <ContentSection
              paragraphs={
                isEnglish ? accuracyParagraphs1Eng : accuracyParagraphs1Id
              }
            />
          </article>

          <div className='w-full grid grid-cols-1 md:grid-cols-2 md:gap-10 md:px-14 my-10'>
            <div>
              <h2 className='text-base md:text-2xl font-bold mb-4'>
                Agate TBS Grading Confusion Matrix
              </h2>
              <figure>
                <img
                  src={
                    isEnglish
                      ? GradingConfusionMatrixTabelEn
                      : GradingConfusionMatrixTabel
                  }
                  alt='Confusion Matrix Table'
                  className='w-full h-full'
                />
              </figure>
            </div>
            <figure>
              <img
                src={
                  isEnglish
                    ? GradingConfusionMatrixStatusEn
                    : GradingConfusionMatrixStatus
                }
                alt='Confusion Matrix Status'
                className='w-full h-full'
              />
            </figure>
          </div>

          <EvaluationTable isEnglish={isEnglish} />
          <EvaluationMobile isEnglish={isEnglish} />

          <article className='text-neutral-7 text-content leading-normal space-y-8 my-10'>
            <ContentSection
              paragraphs={
                isEnglish ? accuracyParagraphs2Eng : accuracyParagraphs2Id
              }
            />
          </article>

          <div className='md:w-3/4 m-auto my-10'>
            <iframe
              width='100%'
              height='513'
              src='https://www.youtube.com/embed/3LP_ZDm-Eyc?si=lD59Ndn61QH0uGv2'
              title='YouTube video player'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              referrerPolicy='strict-origin-when-cross-origin'
              allowFullScreen
            ></iframe>
          </div>
        </section>

        <Footer />
      </main>

      <button
        className='bg-mainGreen hover:bg-mainGray-background hover:text-mainGreen h-10 w-10 md:h-12 md:w-12 fixed bottom-4 right-4 md:bottom-8 md:right-8 cursor-pointer text-white font-medium text-xl md:text-2xl rounded-lg shadow-md active:outline-none focus:outline-none'
        onClick={() => {
          window.scrollTo(0, 0);
          //   window.location.href = '#';
        }}
      >
        &uarr;
      </button>
    </>
  );
}
